import type { PropsWithChildren, ReactNode } from "react";
import React from "react";
import { Box, Button, Chip, Divider, Grid, Stack, Typography } from "@mui/material";
import { getOrParseDateTime } from "@cpt/shared";
import { formatMoney } from "utils/formatters/CurrencyFormatters";
import { formatDateOnly } from "utils/formatters/DateFormatters";
import { formatPercentFromFraction } from "utils/formatters/PercentFormatters";
import { PricingVector } from "client/api-v2";
import type { RateCardAssignmentHistoryEntry } from "client/api-v2";
import { PolicyAuthorized } from "components/authorization/PolicyAuthorized";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { RATE_CARD_STATUS_COLOR } from "../constants/rate-card-status-color";

const allVectors = Object.values(PricingVector);

// The engine only rounds when the value is > 0, so a stored 0 means "no round-down", same as null.
function roundDownToNearestOrUndefined(entry: RateCardAssignmentHistoryEntry): number | undefined {
  return entry.finalPriceRoundDownToNearest ? entry.finalPriceRoundDownToNearest : undefined;
}

function formatDate(value: string): string {
  return formatDateOnly(getOrParseDateTime(value));
}

function formatEffectiveRange(entry: RateCardAssignmentHistoryEntry): string {
  if (!entry.effectiveFrom) return "Not yet effective";

  const from = formatDate(entry.effectiveFrom);
  return entry.effectiveTo ? `Effective ${from} → ${formatDate(entry.effectiveTo)}` : `Effective ${from} → present`;
}

function AssignmentHeading({ entry, children }: PropsWithChildren<{ entry: RateCardAssignmentHistoryEntry }>) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
      <Chip label={entry.status} size="small" color={RATE_CARD_STATUS_COLOR[entry.status]} />
      <Typography variant="subtitle1" sx={{ fontFamily: "monospace" }}>
        {entry.rateCardCode}
      </Typography>
      {entry.label && (
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {entry.label}
        </Typography>
      )}
      <Box sx={{ flexGrow: 1 }} />
      {children}
    </Stack>
  );
}

function TermsColumn({ title, children }: PropsWithChildren<{ title: string }>) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="overline" sx={{ color: "text.secondary" }}>
        {title}
      </Typography>
      {children}
    </Stack>
  );
}

function UnitPrices({ entry }: { entry: RateCardAssignmentHistoryEntry }): ReactNode {
  return allVectors.map((vector) => {
    const unitPrice = entry.unitPrices.find((price) => price.vector === vector);

    return unitPrice ? (
      <Typography key={vector} variant="body2">
        {vector} {formatMoney(unitPrice.listPricePerUnit, entry.currency)}
      </Typography>
    ) : (
      <Typography key={vector} variant="body2" sx={{ color: "text.disabled" }}>
        {vector} — not priced
      </Typography>
    );
  });
}

function AssignmentCard({
  entry,
  action,
  subheading,
}: {
  entry: RateCardAssignmentHistoryEntry;
  action: ReactNode;
  subheading?: ReactNode;
}) {
  const roundDownTo = roundDownToNearestOrUndefined(entry);

  return (
    <Box
      role="group"
      aria-label={`${entry.status} rate card`}
      sx={{ border: 1, borderColor: "divider", borderRadius: 1, padding: 2 }}
    >
      <AssignmentHeading entry={entry}>{action}</AssignmentHeading>
      {subheading}
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 3 }}>
          <TermsColumn title="Unit prices">
            <UnitPrices entry={entry} />
          </TermsColumn>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TermsColumn title="Discounts">
            {entry.discountExponent === 0 ? (
              <Typography variant="body2">No volume discount</Typography>
            ) : (
              <>
                <Typography variant="body2">Exponent V = {entry.discountExponent}</Typography>
                <Typography variant="body2">Threshold {entry.discountThresholdUnits} units</Typography>
              </>
            )}
            {entry.overallDiscountFraction > 0 && (
              <Typography variant="body2">
                {formatPercentFromFraction(entry.overallDiscountFraction)} off the total
              </Typography>
            )}
          </TermsColumn>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TermsColumn title="Fixed fees">
            {entry.fixedFees.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.disabled" }}>
                None
              </Typography>
            ) : (
              // Fee descriptions aren't unique, so the index disambiguates the key.
              entry.fixedFees.map((fee, index) => (
                <Typography key={`${index}-${fee.description}`} variant="body2">
                  {fee.description} {formatMoney(fee.amount, entry.currency)}
                </Typography>
              ))
            )}
          </TermsColumn>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TermsColumn title="Round-down / currency">
            <Typography variant="body2">
              {roundDownTo === undefined ? "No round-down" : formatMoney(roundDownTo, entry.currency)} ·{" "}
              {entry.currency}
            </Typography>
          </TermsColumn>
        </Grid>
      </Grid>
    </Box>
  );
}

function DraftAssignment({
  entry,
  onActivate,
  activating,
}: {
  entry: RateCardAssignmentHistoryEntry;
  onActivate: () => void;
  activating: boolean;
}) {
  return (
    <AssignmentCard
      entry={entry}
      action={
        <PolicyAuthorized policies={RbacPolicies.RateCards.ReadWrite}>
          <Button variant="contained" size="small" onClick={onActivate} disabled={activating}>
            Activate
          </Button>
        </PolicyAuthorized>
      }
      subheading={
        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
          Drafted {formatDate(entry.createdDate)} by {entry.createdByUser} — Reason: &quot;{entry.reason}&quot;
        </Typography>
      }
    />
  );
}

function ActiveAssignment({ entry }: { entry: RateCardAssignmentHistoryEntry }) {
  return (
    <AssignmentCard
      entry={entry}
      action={
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {formatEffectiveRange(entry)}
        </Typography>
      }
    />
  );
}

interface SubscriptionCurrentRateCardProps {
  active?: RateCardAssignmentHistoryEntry;
  draft?: RateCardAssignmentHistoryEntry;
  onActivateDraft: () => void;
  activating: boolean;
}

export function SubscriptionCurrentRateCard({
  active,
  draft,
  onActivateDraft,
  activating,
}: SubscriptionCurrentRateCardProps) {
  return (
    <Stack spacing={3} role="region" aria-label="Current rate card">
      {draft && <DraftAssignment entry={draft} onActivate={onActivateDraft} activating={activating} />}
      {active && <ActiveAssignment entry={active} />}
    </Stack>
  );
}
